/* tslint:disable:max-line-length */
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Injectable} from '@angular/core';
import {MmCompanyDependencyService, NotificationSenderEnum} from './mm-company-dependency.service';
import {FaultField} from '../fault-field';
import {DynamicFormReadyEvent, FilterSelectFieldConfig, SelectItem} from '@mydocma/dynamic-form';
import {BehaviorSubject, combineLatest, firstValueFrom, iif, Observable, of, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {TranslateService} from '@ngx-translate/core';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {FaultDependencyEnum} from './fault-dependency-enum';
import {Craft} from '@docma-mm-core/craft/craft';
import {Company} from '@docma-mm-core/company/company';


@Injectable()
@UntilDestroy()
export class MmOrderService {

  isNotificationSuspended = false;
  removeClearButton?: boolean = undefined;

  fault: FaultData;

  orderField: FaultField;
  orderFieldConfig: FilterSelectFieldConfig;
  formReadyEvent: DynamicFormReadyEvent;

  orders = new Observable<SelectItem[]>();
  allCompanies: SelectItem[];
  allCrafts: SelectItem[];
  allOrders: SelectItem[];
  deactivatedCompanies: SelectItem[];
  deactivatedOrders: SelectItem[];

  changedSubscriptions: Subscription[] = [];

  orderInfoMessageSubject = new BehaviorSubject<FaultDependencyEnum[]>(null);
  orderInfoMessage$ = this.orderInfoMessageSubject.asObservable();

  constructor(private readonly companyDependencyService: MmCompanyDependencyService,
              private readonly translateService: TranslateService) {

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(lang => {
        this.orderInfoMessageSubject.next(this.orderInfoMessageSubject.value);
      });
  }

  preInitialize() {
    if (this.changedSubscriptions && (this.changedSubscriptions?.length ?? 0) > 0) {
      this.changedSubscriptions?.forEach(s => {
        s.unsubscribe();
      });
      this.changedSubscriptions = [];
    }
  }

  async initialize(fault: FaultData, orderField: FaultField, hasCompanyField: boolean, hasCraftField: boolean, orderFieldConfig: FilterSelectFieldConfig, formReadyEvent: DynamicFormReadyEvent) {
    this.fault = fault;
    this.orderField = orderField;
    this.orderFieldConfig = orderFieldConfig;
    this.formReadyEvent = formReadyEvent;

    const result = await firstValueFrom(combineLatest([
                                              iif(() => hasCompanyField, this.companyDependencyService.allCompanies, of<{id: number, text: string, data: Company}[]>([])),
                                              this.companyDependencyService.allOrders,
                                              iif(() => hasCraftField, this.companyDependencyService.allCrafts, of<{id: number, text: string, data: Craft}[]>([])),
                                              iif(() => hasCompanyField, this.companyDependencyService.getDeactivatedCompanies(this.fault.Company?.Id), of<{id: number, text: string, data: Company}[]>([])),
                                              this.companyDependencyService.getDeactivatedOrders(this.fault.Order?.Id, this.fault.Order?.Company?.Id),
                                              iif(() => hasCompanyField, this.companyDependencyService.selectedCompany$, of<{value: SelectItem, sender: NotificationSenderEnum}>(null)),
                                              this.companyDependencyService.selectedOrder$,
                                              iif(() => hasCraftField, this.companyDependencyService.selectedCraft$, of<{value: SelectItem, sender: NotificationSenderEnum}>(null)),
                                              this.companyDependencyService.loadRecentlyUsedEntities(PersonalizationKeysEnum.RecentlyUsedOrders)])
      .pipe(untilDestroyed(this),
        map(async ([allCompanies, allOrders, allCrafts, deactivatedCompanies, deactivatedOrders, selectedCompany, selectedOrder, selectedCraft]) => {
          this.allCompanies = allCompanies;
          this.allCrafts = allCrafts;
          this.allOrders = allOrders;
          this.deactivatedCompanies = deactivatedCompanies;
          this.deactivatedOrders = deactivatedOrders;


          const result = await this.processDependencies(selectedCraft, selectedCompany, this.companyDependencyService.recentlyUsedOrders, NotificationSenderEnum.initialize);
          return result;
        })
      ));

    this.orders = of(result.orders);
    await this.updateFieldConfig();

    this.changedSubscriptions.push(this.companyDependencyService.selectedOrder$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender === NotificationSenderEnum.order) {
          await this.setInfoMessage(param, false);
        }
      }));

    this.changedSubscriptions.push(this.companyDependencyService.selectedCompany$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender !== NotificationSenderEnum.initialize && orderFieldConfig) {
          const selectedCraft = await firstValueFrom(this.companyDependencyService.selectedCraft$);
          const selectedCompany = param;
          const result = await this.processDependencies(selectedCraft, selectedCompany, this.companyDependencyService.recentlyUsedOrders, param.sender);
          this.orders = of(result.orders);
          await this.updateFieldConfig();

          const currentOrder = this.formReadyEvent.dynamicFormService.getFieldValue(orderFieldConfig.name);

          // param.sender !== NotificationSenderEnum.order &&
          if (result.selectOrder && result.selectOrder.id !== currentOrder && !currentOrder && selectedCompany.value) {
            this.formReadyEvent.dynamicFormService.updateFieldValue(orderFieldConfig.name, result.selectOrder.id, false);
            this.companyDependencyService.selectedOrderSubject.next({
              sender: param.sender,
              value: result.selectOrder
            });
            this.companyDependencyService.onFieldValueChanged.emit();
          }

          const onlyReset = (param.sender === NotificationSenderEnum.craft || param.sender === NotificationSenderEnum.company);
          await this.setInfoMessage(param, onlyReset);
        }
      }));

    this.changedSubscriptions.push(this.companyDependencyService.selectedCraft$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender !== NotificationSenderEnum.initialize && orderFieldConfig) {
          const selectedCraft = param;
          const selectedCompany = await firstValueFrom(this.companyDependencyService.selectedCompany$);
          const result = await this.processDependencies(selectedCraft, selectedCompany, this.companyDependencyService.recentlyUsedOrders, param.sender);
          this.orders = of(result.orders);
          await this.updateFieldConfig();

          const currentOrder = this.formReadyEvent.dynamicFormService.getFieldValue(orderFieldConfig.name);

          if (param.sender !== NotificationSenderEnum.order && result.selectOrder && result.selectOrder.id !== currentOrder && !currentOrder && selectedCompany.value) {
            this.formReadyEvent.dynamicFormService.updateFieldValue(orderFieldConfig.name, result.selectOrder.id, false);
            this.companyDependencyService.selectedOrderSubject.next({
              sender: param.sender,
              value: result.selectOrder
            });
            this.companyDependencyService.onFieldValueChanged.emit();
          }

          const onlyReset = (param.sender === NotificationSenderEnum.craft || param.sender === NotificationSenderEnum.company);
          await this.setInfoMessage(param, onlyReset);
        }
      }));
  }

  async updateFieldConfig() {
    if (this.orderFieldConfig) {
      this.orderFieldConfig.groupedSource = true;
      this.orderFieldConfig.removeClearButton = (this.removeClearButton ? this.removeClearButton : this.orderFieldConfig.readonly);
      this.orderFieldConfig.source = await firstValueFrom(this.orders);
      this.formReadyEvent.dynamicFormService.updateFieldConfig(this.orderFieldConfig);
    }
  }

  async processDependencies(selectedCraft: {value: any, sender: NotificationSenderEnum}, selectedCompany: {value: any, sender: NotificationSenderEnum}, recentlyUsedOrders: { [index: number]: number }, sender: NotificationSenderEnum) {
    const groupItems: {[id: number]: SelectItem} = [];
    let selectOrder: SelectItem;
    const allCompanies = (this.deactivatedCompanies ?? []).concat(this.allCompanies);
    const allOrders = (this.deactivatedOrders ?? []).concat(this.allOrders);


    const craft = (selectedCraft?.value?.id ? this.allCrafts.find(x => x.id === selectedCraft.value.id) : null);
    const company = (selectedCompany?.value?.id ? allCompanies.find(x => x.id === selectedCompany.value.id) : null);

    if (craft && company) {
      // FILTERED + LAST USED + ALL
      selectOrder = this.addCompanyCraftOrders(allOrders, company, craft, groupItems);
      this.companyDependencyService.addRecentlyUsedEntities(allOrders, recentlyUsedOrders, groupItems);
      this.addAllOrders(allOrders, groupItems, craft, company);
    }
    else if (craft && !company) {
      // FILTERED + LAST USED + ALL
      selectOrder = this.addCraftOrders(allOrders, craft, groupItems);
      this.companyDependencyService.addRecentlyUsedEntities(allOrders, recentlyUsedOrders, groupItems);
      this.addAllOrders(allOrders, groupItems, craft, company);
    }
    else if (!craft && company) {
      selectOrder = this.addCompanyOrders(allOrders, company, groupItems);
      this.companyDependencyService.addRecentlyUsedEntities(allOrders, recentlyUsedOrders, groupItems);
      this.addAllOrders(allOrders, groupItems, craft, company);
    }
    else {
      // LAST USED + ALL
      this.companyDependencyService.addRecentlyUsedEntities(allOrders, recentlyUsedOrders, groupItems);
      selectOrder = this.addAllOrders(allOrders, groupItems, craft, company, true);
    }

    const items = (groupItems && Object.keys(groupItems).length > 0 ? Object.values(groupItems) : allOrders);
    return {orders: items, selectOrder};
  }

  addCraftOrders(allOrders: SelectItem[], craft: SelectItem, groupItems: {[id: number]: SelectItem}) {
    const items: SelectItem[] = allOrders.filter(x => x.data?.Craft && x.data.Craft.Id === craft.id);

    if ((items?.length ?? 0) > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup1').replace('ENTITY', craft.text);
      groupItems[0] = {id: 0, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addCompanyCraftOrders(allOrders: SelectItem[], company: SelectItem, craft: SelectItem , groupItems: {[id: number]: SelectItem}) {
    const items: SelectItem[] = allOrders.filter(x => x.data?.Craft && x.data.Craft.Id === craft.id && (x.data.Company.Id === company.id || x.data.Company.Id === company.data.Parent?.Id));

    if ((items?.length ?? 0) > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup1').replace('ENTITY', company.text).replace('ENTITY2', craft.text);
      groupItems[0] = {id: 0, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addCompanyOrders(allOrders: SelectItem[], company: SelectItem, groupItems: {[id: number]: SelectItem}) {
    const items = allOrders.filter(o => (o.data.Company.Id === company.id || o.data.Company.Id === company.data.Parent?.Id))
      .map(o => ({id: o.id, text: o.text, data: o}))
      .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []);

    if ((items?.length ?? 0) > 0) {  // IF ORDERS: FILTERED + LAST USED + ALL
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup1').replace('ENTITY', company.text);
      groupItems[0] = {id: 0, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addAllOrders(allOrders: SelectItem[], groupItems: {[id: number]: SelectItem}, craft: SelectItem, company: SelectItem, returnSelectedOrder: boolean = false) {
    let selectedItem: SelectItem;

    if (returnSelectedOrder) {
      const items = allOrders.filter(x => (craft && x.id === craft.id) || (company && (x.id === company.id || x.id === company.data.Parent?.Id)));
      if ((items?.length ?? 0) === 1) {
        selectedItem = items[0];
      }
    }

    if (groupItems && Object.keys(groupItems).length > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyAllGoup');
      groupItems[2] = {id: 2, text: nameFunc(), data: nameFunc};
      groupItems[2].items = allOrders;
    }

    if (returnSelectedOrder) {
      return selectedItem;
    }
  }

  async setInfoMessage(param: {value: SelectItem, sender: NotificationSenderEnum}, onlyReset: boolean) {
    if (param.sender !== NotificationSenderEnum.initialize) {
      let infoMessage: FaultDependencyEnum[] = null;

      const selectedCompany = await firstValueFrom(this.companyDependencyService.selectedCompany$);
      const selectedCraft = await firstValueFrom(this.companyDependencyService.selectedCraft$);
      const selectedOrder = await firstValueFrom(this.companyDependencyService.selectedOrder$);

      const company = (selectedCompany?.value?.id ? this.allCompanies.find(x => x.id === selectedCompany.value.id) : null);
      const craft = (selectedCraft?.value?.id ? this.allCrafts.find(x => x.id === selectedCraft.value.id) : null);

      const order = (selectedOrder?.value?.id ? this.allOrders.find(x => x.id === selectedOrder.value.id) : null);

      if (craft && company) {
        const items = this.allOrders.filter(o => o.data?.Craft && o.data?.Craft.Id === craft.id && (o.data?.Company.Id === company.id || o.data?.Company?.Id === company.data.Parent?.Id))
          .map(c => ({id: c.data?.Craft.Id, text: (c.data?.Craft.Name1 + (c.data?.Craft.Name2 ? ' - ' + c.data?.Craft.Name2 : '')), data: c.data}))
          .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []);

        if ((items?.length ?? 0) === 1) {
          if (order && (order.data.Company.Id !== company.id && order.data?.Company?.Id !== company.data.Parent?.Id)) {
            // console.warn('ORDER.MESSAGE #2');
            infoMessage = [FaultDependencyEnum.Company];
          }
          else if (order && (order.data?.Company?.Id !== company.id && order.data?.Company?.Id !== company.data.Parent?.Id) && order.data?.Craft?.Id !== craft.id) {
            // console.warn('ORDER.MESSAGE #2');
            infoMessage = [FaultDependencyEnum.Company];
          }
          else if (order && (order.data?.Company?.Id === company.id || order.data?.Company?.Id === company.data.Parent?.Id) && order.data?.Craft?.Id !== craft.id) {
            // console.warn('ORDER.MESSAGE #3');
            infoMessage = [FaultDependencyEnum.Company];
          }
        }
        else if ((items?.length ?? 0) === 0 && order) {
          // console.warn('ORDER.MESSAGE #1');
          infoMessage = [FaultDependencyEnum.Company, FaultDependencyEnum.Order];
        }
      }
      else if (craft && !company) {
        const items = this.allOrders.filter(o => o.data?.Craft && o.data?.Craft.Id === craft.id)
          .map(c => ({id: c.data.Craft.Id, text: (c.data?.Craft.Name1 + (c.data?.Craft.Name2 ? ' - ' + c.data?.Craft.Name2 : ''))}))
          .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []);

        if (order && !items.some(x => x.id === craft?.id)) {
          // console.warn('ORDER.MESSAGE #3');
          infoMessage = [FaultDependencyEnum.Craft];
        }
      }
      else if (!craft && company) {
        if (order && (order.data?.Company.Id !== company.id && order.data?.Company?.Id !== company.data.Parent?.Id)) {
          // console.warn('ORDER.MESSAGE #2');
          infoMessage = [FaultDependencyEnum.Company];
        }
        else if (order && !this.allOrders.some(o => (o.data?.Company.Id === company.id || order.data?.Company?.Id === company.data.Parent?.Id))) {
          // console.warn('ORDER.MESSAGE #2');
          infoMessage = [FaultDependencyEnum.Company];
        }
      }
      else {
        // NO MESSAGE!
      }

      if (!onlyReset || (onlyReset && !infoMessage)) {
        // console.warn('ORDER.MESSAGE ' + infoMessage);
        this.orderInfoMessageSubject.next(infoMessage);
      }
    }
  }

  async reloadOrders() {
    const selectedCompany = await firstValueFrom(this.companyDependencyService.selectedCompany$);
    const selectedCraft = await firstValueFrom(this.companyDependencyService.selectedCraft$);
    const result = await this.processDependencies(selectedCraft, selectedCompany, this.companyDependencyService.recentlyUsedOrders, NotificationSenderEnum.initialize);
    this.orders = of(result.orders);
    await this.updateFieldConfig();
  }
}
